import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import type { JournalCardPayload, JournalEntry, JournalWarmupState } from '@domain/journal';
import SelectedCard from './SelectedCard';
import { JOURNAL_STEP_ICONS } from './stepIcons';
import { MoodMetricGauge } from './MoodMetricGauge';

export type ReviewStepProps = {
  entry: JournalEntry;
  isConfirming?: boolean;
  onBack?: () => void;
  onConfirm?: () => void;
  showActions?: boolean;
  title?: string;
  description?: string;
  emptyLabel?: string;
};

const sanitizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getWarmupState = (warmup?: JournalWarmupState | null) => {
  if (!warmup) {
    return {
      moodLevel: null,
      energyLevel: null,
      moodFeedback: null,
      energyFeedback: null,
    };
  }

  return {
    moodLevel:
      typeof warmup.moodLevel === 'number' && Number.isFinite(warmup.moodLevel)
        ? warmup.moodLevel
        : null,
    energyLevel:
      typeof warmup.energyLevel === 'number' && Number.isFinite(warmup.energyLevel)
        ? warmup.energyLevel
        : null,
    moodFeedback: sanitizeString(warmup.moodFeedback ?? null),
    energyFeedback: sanitizeString(warmup.energyFeedback ?? null),
  };
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const lowered = value.toLowerCase();
    return lowered === 'true' || lowered === 'yes' || lowered === '1';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return fallback;
};

const ensureCardPayload = (value: unknown, defaultSelected = false): JournalCardPayload | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const id = toNumber(record.id ?? record['card_id'] ?? record['journalCardId']);
  if (id == null) {
    return null;
  }

  const text = sanitizeString(record.text ?? record['card_text']) ?? 'Card';
  const category = sanitizeString(record.category ?? record['card_category']) ?? 'Generale';
  const selected = toBoolean(record.selected, defaultSelected);

  return {
    id,
    text,
    category,
    selected,
  };
};

const extractResponses = (entry: JournalEntry): unknown[] => {
  if (Array.isArray(entry.cardResponses) && entry.cardResponses.length > 0) {
    return entry.cardResponses;
  }
  const fallback = (entry as unknown as Record<string, unknown>)['card_responses'];
  return Array.isArray(fallback) ? fallback : [];
};

const getSelectedCards = (entry: JournalEntry): JournalCardPayload[] => {
  const hand = Array.isArray(entry.hand) ? entry.hand : [];
  const normalizedHand = hand
    .map((card) => ensureCardPayload(card, Boolean(card?.selected)))
    .filter((card): card is JournalCardPayload => card != null);
  const selectedFromHand = normalizedHand.filter((card) => card.selected);

  if (selectedFromHand.length > 0) {
    return selectedFromHand;
  }

  const responses = extractResponses(entry);
  const selectedFromResponses: JournalCardPayload[] = [];

  responses.forEach((response) => {
    if (!response || typeof response !== 'object') {
      return;
    }
    const record = response as Record<string, unknown>;
    const responseValue = sanitizeString(record.responseValue ?? record['response_value']);
    if (responseValue !== 'yes') {
      return;
    }
    const card = ensureCardPayload(record.card, true);
    if (card) {
      selectedFromResponses.push({
        ...card,
        selected: true,
      });
    }
  });

  return selectedFromResponses;
};

const getWritingContent = (entry: JournalEntry): string | null => {
  const writing = entry.writing ?? {};
  const finalText = 'finalText' in writing ? writing.finalText : null;
  return sanitizeString(finalText ?? null);
};

const getCommitmentText = (entry: JournalEntry): string | null => {
  const writing = entry.writing ?? {};
  const text = 'commitmentText' in writing ? writing.commitmentText : null;
  return sanitizeString(text ?? null);
};

const hasTherapistAlert = (entry: JournalEntry): boolean => {
  const writing = entry.writing ?? {};
  const flag = 'alertTherapist' in writing ? writing.alertTherapist : null;
  return Boolean(flag);
};

export default function ReviewStep({
  entry,
  isConfirming = false,
  onBack,
  onConfirm,
  showActions,
  title,
  description,
  emptyLabel = 'Sto preparando il riepilogo...',
}: ReviewStepProps) {
  const StepIcon = JOURNAL_STEP_ICONS.completed;
  const heading = title ?? 'Riepilogo della giornata';
  const subheading =
    description ??
    'Controlla il riepilogo generato prima di confermarlo. Dopo la conferma sarà visibile al tuo psicologo.';

  const warmup = getWarmupState(entry.warmup);
  const hasWarmupSection =
    warmup.moodLevel != null ||
    warmup.energyLevel != null ||
    Boolean(warmup.moodFeedback) ||
    Boolean(warmup.energyFeedback);

  const selectedCards = getSelectedCards(entry);
  const hasCards = selectedCards.length > 0;

  const writingContent = getWritingContent(entry);
  const hasWritingSection = Boolean(entry.writing);
  const commitmentText = getCommitmentText(entry);
  const therapistAlert = hasTherapistAlert(entry);

  const hasContent =
    hasWarmupSection || hasCards || hasWritingSection || Boolean(commitmentText) || therapistAlert;

  return (
    <>
      <Heading level={2} icon={StepIcon} description={subheading} wrapperClassName="mb-4">
        {heading}
      </Heading>

      {hasContent ? (
        <div className="space-y-4">
          {hasWarmupSection && (
            <Card className="space-y-4 rounded-2xl border border-muted-foreground/10 bg-muted/30 p-5">
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Come stai?
                </h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <MoodMetricGauge
                  label="Umore"
                  value={warmup.moodLevel}
                  feedback={warmup.moodFeedback}
                />
                <MoodMetricGauge
                  label="Energia"
                  value={warmup.energyLevel}
                  feedback={warmup.energyFeedback}
                />
              </div>
            </Card>
          )}

          {hasCards && (
            <Card className="rounded-2xl border border-muted-foreground/10 bg-muted/30 p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Punti emersi
              </h3>
              <div className="mt-4 flex flex-wrap gap-4">
                {selectedCards.map((card) => (
                  <SelectedCard
                    key={`summary-selected-card-${card.id}`}
                    text={card.text ?? 'Card'}
                    category={card.category}
                  />
                ))}
              </div>
            </Card>
          )}

          <Card className="rounded-2xl border border-muted-foreground/10 bg-muted/30">
            <CardHeader>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Pagina del diario
              </h3>
            </CardHeader>
            <CardContent className="whitespace-pre-wrap leading-relaxed">
              {writingContent ?? 'Nessun testo disponibile'}
            </CardContent>
          </Card>

          {commitmentText && (
            <Card className="rounded-2xl border border-muted-foreground/10 bg-muted/30 p-5 space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Impegno
              </h3>
              <p className="text-sm text-foreground">{commitmentText}</p>
            </Card>
          )}

          {therapistAlert && (
            <Card className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 space-y-2">
              <h3 className="text-sm font-semibold text-destructive uppercase tracking-wide">
                Alert psicologo attivo
              </h3>
              <p className="text-sm text-destructive/80">
                Questo diario è stato contrassegnato per chiedere un contatto con lo psicologo.
              </p>
            </Card>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      )}

      {showActions !== false && (onBack || onConfirm) && (
        <div className="flex justify-end gap-3">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              Torna alla bozza
            </Button>
          )}
          {onConfirm && (
            <Button onClick={onConfirm} disabled={isConfirming || !entry}>
              Conferma e invia
            </Button>
          )}
        </div>
      )}
    </>
  );
}
